import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { getListChildren } from 'api/endpoints/children';
import LayoutMain from 'components/layouts/LayoutMain';
import { OPTION_PAGINATION } from 'constants/index';
import UsersListHeader from 'pages/Users/List/UsersHeader';
import UserPagination from 'pages/Users/List/UsersPagination';
import UsersSearch from 'pages/Users/List/UsersSearch';
import UsersTable from 'pages/Users/List/UsersTable';
import { convertToQueryString } from 'utils/helper';

const User = () => {
  const { t } = useTranslation();
  const { me } = useSelector((state) => state.auth);

  const [options, setOptions] = useState({ keyword: null, ...OPTION_PAGINATION });
  const [sort, setSort] = useState([]);
  const [listUsers, setListUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  const isCallingApi = useRef(false);

  // SEARCH
  const onChangeKeyword = (keyword) => {
    if (keyword !== options.keyword) {
      setOptions((prevState) => {
        return { ...prevState, keyword: keyword };
      });
    }
  };

  // PAGINATION
  const onPageChange = (e) => {
    if (e !== options.page) {
      setOptions((prevState) => {
        return { ...prevState, page: e };
      });
      return true;
    }

    return false;
  };

  // SORT
  const onSortChange = (e) => {
    setSort(() => e);
  };

  /**
   * @name handleSort
   * @returns {string[]}
   * @example ['title', '-updated_at']
   * @example ['-title', 'updated_at']
   */
  const handleSort = () => {
    return (
      sort.map((item) => {
        return item.desc ? `-${item.id}` : item.id;
      }) || []
    );
  };

  const handleGetChildren = async () => {
    if (!isCallingApi.current) {
      isCallingApi.current = true;
      const sortHandle = handleSort();
      const queryString = convertToQueryString({ keyword: options.keyword, sort_by: sortHandle, page: options.page, page_size: options.page_size });
      setLoading(true);
      try {
        await getListChildren(queryString)
          .then((response) => {
            const totalPage = Math.ceil(response.total / response.page_size);
            const page = response.page > totalPage ? (totalPage === 0 ? 1 : totalPage) : options.page;

            setListUsers(response?.data || []);
            setOptions((prevState) => {
              return {
                ...prevState,
                total: response.total,
                page_size: response.page_size,
                page: page,
              };
            });
            setLoading(false);
          })
          .catch(() => {
            setLoading(false);
          });
      } catch {
        setLoading(false);
      } finally {
        isCallingApi.current = false;
      }
    }
  };

  // GET DATA
  const getChildren = useCallback(async () => {
    if (me) {
      handleGetChildren();
    }
  }, [options.page, options.keyword, sort, me]);

  useEffect(() => {
    getChildren();
  }, [getChildren]);

  return (
    <>
      <LayoutMain className={'!max-w-1720'}>
        <section id="question-list">
          <h1 className="text-2xl font-bold text-black/85">{t('USER_LIST')}</h1>
          <UsersListHeader options={options} />
          <UsersSearch options={options} onSubmit={onChangeKeyword} />
          <div className={`w-full mt-4 ${options.total > 0 ? 'min-h-164.75' : ''}`}>
            <UsersTable
              data={listUsers}
              setSort={onSortChange}
              sort={sort}
              page={options.page}
              keyword={options.keyword}
              isFilter={!!options.keyword || options.municipality_code}
              reloadData={handleGetChildren}
            />
          </div>
          {options.total > 0 && <UserPagination options={options} onPageChange={onPageChange} loading={loading} />}
        </section>
      </LayoutMain>
    </>
  );
};

export default React.memo(User);
